import { useCallback, useEffect, useMemo, useState } from "react";
import type { DueOccurrence } from "../lib/schedule";

const NOTIFIED_KEY = "ritual_notified_occurrences_v1";

function readNotified() {
  try {
    return new Set<string>(
      JSON.parse(localStorage.getItem(NOTIFIED_KEY) || "[]") as string[],
    );
  } catch {
    return new Set<string>();
  }
}

function writeNotified(keys: Set<string>) {
  localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...keys].slice(-500)));
}

function canNotify() {
  return "Notification" in window && "serviceWorker" in navigator;
}

function isStandaloneApp() {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };
  return Boolean(
    window.matchMedia("(display-mode: standalone)").matches ||
    standaloneNavigator.standalone,
  );
}

export function useReminderNotifications(occurrences: DueOccurrence[]) {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    "Notification" in window ? Notification.permission : "default",
  );
  const [notified, setNotified] = useState(() => readNotified());

  const missed = useMemo(
    () =>
      occurrences.filter(
        (occurrence) => occurrence.status === "missed" && occurrence.notify,
      ),
    [occurrences],
  );

  const requestPermission = useCallback(async () => {
    if (!canNotify()) return "denied" as NotificationPermission;
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  useEffect(() => {
    if ("setAppBadge" in navigator) {
      const badgeCount = occurrences.filter(
        (occurrence) =>
          occurrence.status === "missed" || occurrence.status === "open",
      ).length;
      if (badgeCount > 0) {
        void (
          navigator as Navigator & {
            setAppBadge: (count: number) => Promise<void>;
          }
        ).setAppBadge(badgeCount);
      } else if ("clearAppBadge" in navigator) {
        void (
          navigator as Navigator & { clearAppBadge: () => Promise<void> }
        ).clearAppBadge();
      }
    }
  }, [occurrences]);

  useEffect(() => {
    if (permission !== "granted" || missed.length === 0 || !canNotify()) return;

    let cancelled = false;
    async function notify() {
      const registration = await navigator.serviceWorker.ready;
      const nextNotified = new Set(notified);

      for (const occurrence of missed) {
        if (cancelled || nextNotified.has(occurrence.key)) continue;

        await registration.showNotification("Ritual reminder", {
          body: `${occurrence.title} was due ${occurrence.label} (${occurrence.startTime}-${occurrence.endTime}).`,
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag: occurrence.key,
          data: { url: "/" },
        });
        nextNotified.add(occurrence.key);
      }

      setNotified(nextNotified);
      writeNotified(nextNotified);
    }

    void notify().catch((error) =>
      console.warn("Reminder notification failed.", error),
    );

    return () => {
      cancelled = true;
    };
  }, [missed, notified, permission]);

  return {
    permission,
    supported: canNotify(),
    pushSupported: "PushManager" in window,
    standalone: isStandaloneApp(),
    requestPermission,
  };
}
